import { useMemo, useState } from 'react';

import {
  ZfDialog,
  ZfDialogContent,
  ZfDialogFooter,
  ZfDialogHeader,
  ZfLoadingSpinner,
  ZfStack,
  ZfTextButton,
} from '@zafire/react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { updatePortfolio } from 'services/portfolios/portfolioService';
import { closeModal } from '../../reducers/modalReducer';
import { PortfolioDataResponse } from '../../types/portfolios/portfolioData';
import EditPortfolioDetailSection from './EditPortfolioDetailSection';
import { CashAccount } from './AddPortfolioDialog';
import PortfolioEditSchema from './portfolioEditSchema';

type EditPortfolioDialogType = {
  portfolio: PortfolioDataResponse;
  brUid: string;
  onProceed?: () => void;
};

export type EditPortfolioForm = {
  referenceCurrency: string;
  portfolioType: string;
  portfolioTypeNumber: string;
  sequenceNumber: string;
  rubrik: string;
  details: string;
  manual: boolean;
  allocatedBo: string;
  chargingCurrency: string;
  cashAccounts: CashAccount[];
};

const EditPortfolioDialog = ({ portfolio, onProceed, brUid }: EditPortfolioDialogType) => {
  const initialValues = useMemo(() => {
    return {
      referenceCurrency: portfolio.referenceCurrency,
      portfolioType: portfolio.portfolioType,
      portfolioTypeNumber: portfolio.portfolioNumber,
      sequenceNumber: portfolio.portfolioNumber.slice(-2),
      chargingCurrency: portfolio.chargingCurrency,
      rubrik: portfolio.rubrik,
      details: portfolio.details,
      allocatedBo: portfolio.allocatedBo,
      cashAccounts: portfolio.cashAccounts,
    };
  }, [portfolio]);

  const dispatch = useDispatch();

  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleClose = () => {
    dispatch(closeModal());
  };
  return (
    <Formik
      validateOnBlur={false}
      validateOnChange
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={PortfolioEditSchema}
      onSubmit={async (data, { setSubmitting, resetForm }) => {
        setSubmitting(true);

        try {
          await updatePortfolio({ ...portfolio, ...data, sequenceNumber: Number(data.sequenceNumber) });
          resetForm();
          setSubmitting(false);
          if (onProceed) {
            onProceed();
          }
          handleClose();
        } catch (e: unknown) {
          const error = e as { response?: { data?: { title?: string } } };

          const message = error?.response?.data?.title || 'Unknown error';

          setErrorMsg(message);
          setShowErrorDialog(true);
          setSubmitting(false);
        }
      }}
    >
      {({ submitForm, resetForm, isSubmitting }) => {
        return (
          <>
            <ZfDialog show={showErrorDialog} size="small" disableLightDismiss>
              <ZfDialogHeader>Error</ZfDialogHeader>
              <ZfDialogContent>
                <ZfStack spacing="medium">{errorMsg}</ZfStack>
              </ZfDialogContent>
              <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                  <ZfTextButton
                    size="medium"
                    hierarchy="tertiary"
                    onClick={() => {
                      setShowErrorDialog(false);
                    }}
                  >
                    Cancel
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogFooter>
            </ZfDialog>
            <ZfDialog show={true} size="medium" onDialogClose={handleClose} disableLightDismiss>
              <ZfDialogHeader>Edit portfolio</ZfDialogHeader>
              <ZfDialogContent>
                <ZfStack spacing="medium">
                  <EditPortfolioDetailSection brUid={brUid || ''} canEditDetails={portfolio.canEditDetails === true} />
                </ZfStack>
              </ZfDialogContent>
              <ZfDialogFooter>
                <ZfStack slot="primary" direction="row" spacing="small">
                  <ZfTextButton
                    size="medium"
                    hierarchy="tertiary"
                    onClick={() => {
                      handleClose();
                      resetForm();
                    }}
                  >
                    Cancel
                  </ZfTextButton>
                  <ZfTextButton
                    size="medium"
                    onClick={async () => {
                      if (!isSubmitting) {
                        await submitForm();
                      }
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting && <ZfLoadingSpinner size="small" />}
                    Save
                  </ZfTextButton>
                </ZfStack>
              </ZfDialogFooter>
            </ZfDialog>
          </>
        );
      }}
    </Formik>
  );
};

export default EditPortfolioDialog;
